import type { BTServiceFileUpload } from "legacyComponents/FileUploadContainer.types";
import { useState } from "react";

import { BTText } from "@btnet/core-components";

import { BdsIconTrash } from "@buildertrend/components";

import { TempFileTypes } from "types/enum";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { btConfirm } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import { BTUpload } from "commonComponents/btWrappers/BTUpload/BTUpload";

export interface IFileCustomFieldData {
    documentInstanceId: string;
    fileName: string | null;
    fileExtension: string | null;
    downloadLink: string | null;
    tempId?: number;
}

/**
 * A confirmed file value is a numeric document instance ID from the backend.
 * Pending uploads have empty string or JSON temp file info as the value.
 */
export function isConfirmedFileValue(value: string | undefined | null): boolean {
    if (!value) return false;
    return /^\d+$/.test(value);
}

export function confirmFileRemove(onConfirm: () => void): void {
    btConfirm({
        title: "Remove file?",
        content: "Are you sure you want to remove this file?",
        okText: "Remove",
        cancelText: "Cancel",
        onOk: onConfirm,
    });
}

interface IFileFieldDisplayProps {
    fileData: IFileCustomFieldData;
}

function getDisplayName(fileData: IFileCustomFieldData): string {
    if (fileData.fileName) return fileData.fileName;
    if (fileData.documentInstanceId.startsWith("{")) {
        try {
            const parsed: { fileName?: string } = JSON.parse(fileData.documentInstanceId) as {
                fileName?: string;
            };
            return parsed.fileName ?? "";
        } catch {
            return "";
        }
    }
    return fileData.documentInstanceId;
}

export function FileFieldDisplay({ fileData }: Readonly<IFileFieldDisplayProps>) {
    const displayName = getDisplayName(fileData);

    if (fileData.downloadLink) {
        return (
            <BTExternalLink href={fileData.downloadLink} className="custom-field-link-anchor">
                {displayName}
            </BTExternalLink>
        );
    }

    return <BTText size="normal-md" text={displayName} />;
}

interface IFileFieldEditorProps {
    value: IFileCustomFieldData | null;
    customFieldId: number;
    allowedFileTypes?: string[];
    onChange: (newValue: IFileCustomFieldData | null) => void;
    onBlur: () => void;
}

const defaultAllowedFileTypes = ["*"];

export function FileFieldEditor({
    value,
    customFieldId,
    allowedFileTypes = defaultAllowedFileTypes,
    onChange,
    onBlur,
}: Readonly<IFileFieldEditorProps>) {
    const [uploading, setUploading] = useState(false);

    const handleUploadComplete = (
        _field: string,
        fileUpload: BTServiceFileUpload | BTServiceFileUpload[] | undefined,
    ) => {
        if (!fileUpload || Array.isArray(fileUpload)) return;

        onChange({
            documentInstanceId: "",
            fileName: fileUpload.fileName ?? null,
            fileExtension: fileUpload.ext ?? null,
            downloadLink: null,
            tempId: fileUpload.tempId ?? undefined,
        });
        onBlur();
    };

    const handleRemove = () => {
        confirmFileRemove(() => {
            onChange(null);
            onBlur();
        });
    };

    if (isConfirmedFileValue(value?.documentInstanceId) || value?.tempId) {
        return (
            <div className="custom-field-file-display" style={{ padding: "8px" }}>
                <FileFieldDisplay fileData={value!} />
                <BTButton
                    data-testid="removeFileButton"
                    type="tertiary"
                    onClick={handleRemove}
                    aria-label="Remove file"
                    icon={<BdsIconTrash />}
                />
            </div>
        );
    }

    return (
        <div className="custom-field-file-upload" style={{ padding: "8px" }}>
            <div className="custom-field-file-upload-trigger">
                <BTUpload
                    id={`fileCustomField-${customFieldId}`}
                    data-testid="fileCustomFieldUpload"
                    tempFileType={TempFileTypes.CustomFieldFiles}
                    allowedFileTypes={allowedFileTypes}
                    setFieldValue={handleUploadComplete}
                    value={undefined}
                    useBdsButtonStyle
                    buttonType="tertiary"
                    uploadButtonText=""
                    showUploadList={false}
                    loading={uploading}
                    onStatusChange={(statuses) => {
                        setUploading(statuses.some((s) => s === "uploading"));
                    }}
                />
            </div>
        </div>
    );
}
